"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RegistryReader = void 0;
/*---------------------------------------------------------------------------------------------
*  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
*--------------------------------------------------------------------------------------------*/
const os = require("os");
const path = require("path");
const CommandExecutor_1 = require("../Utils/CommandExecutor");
const CacheTimeConstants_1 = require("./CacheTimeConstants");
const IRegistryReader_1 = require("./IRegistryReader");
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
class RegistryReader extends IRegistryReader_1.IRegistryReader {
    constructor(context, utilContext, executor = null) {
        super();
        this.commandRunner = executor !== null && executor !== void 0 ? executor : new CommandExecutor_1.CommandExecutor(context, utilContext);
    }
    /**
     * @remarks architecture values accepted: x64, arm64. x86, arm32 possible on linux but 32 bit vscode is not supported.
     * @returns the folder containing the host
     */
    getHostLocation(architecture) {
        return __awaiter(this, void 0, void 0, function* () {
            // InstallLocation vs sharedhost:
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
            // The sharedhost registry key stores the path of dotnet that gets put on the PATH.
            // The InstallLocation stores other paths to the host that might not be on the PATH, such as an x64 host on an arm64 machine.
            // The InstallLocation is always under the 32 bit reg node for our purposes, and the sharedhost is always on the native node.
            // (You can't install arm64 SDK or Runtime on x64, but you can do vice versa and they follow the same methodology.)
            // For non native installs: sharedhost nodes may exist, but there will be no path key
            // See https://github.com/dotnet/runtime/issues/109974
            // Docs: https://github.com/dotnet/designs/blob/main/accepted/2021/install-location-per-architecture.md
            const queryForPATHLocationNotViaPATH = `HKEY_LOCAL_MACHINE\\SOFTWARE\\dotnet\\Setup\\InstalledVersions\\${architecture}\\sharedhost`;
            const queryForInstallLocationNotPATH = `HKEY_LOCAL_MACHINE\\SOFTWARE\\dotnet\\Setup\\InstalledVersions\\${architecture}\\InstallLocation`;
            const queryForPATH = os.arch() === architecture;
            let queriedInstallationLocation = false;
            let result = null;
            if (queryForPATH) {
                result = yield this.queryRegistry(queryForPATHLocationNotViaPATH, false, 'Path');
            }
            if (!queryForPATH || (result === null || result === void 0 ? void 0 : result.status) !== '0') {
                result = yield this.queryRegistry(queryForInstallLocationNotPATH, true, 'InstallLocation');
                queriedInstallationLocation = true;
            }
            if ((result === null || result === void 0 ? void 0 : result.status) === '0') {
                // Output is of the type
                // REGISTRY_KEY
                // Key   REG_SZ    C:\path\to\dotnet
                let keyRow = (_d = (_c = (_b = (_a = result.stdout) === null || _a === void 0 ? void 0 : _a.trim()) === null || _b === void 0 ? void 0 : _b.split("\n")) === null || _c === void 0 ? void 0 : _c[1]) === null || _d === void 0 ? void 0 : _d.trim(); // Get the line that contains Key REG_SZ Path
                if (!keyRow) {
                    // If there's an extra newline in the output or reg.exe format changes, find the line which contains the key -- slower
                    keyRow = (_j = (_h = (_g = (_f = (_e = result.stdout) === null || _e === void 0 ? void 0 : _e.trim()) === null || _f === void 0 ? void 0 : _f.split("\n")) === null || _g === void 0 ? void 0 : _g.filter((x) => x.toLowerCase().includes('path') || x.toLowerCase().includes('installlocation'))) === null || _h === void 0 ? void 0 : _h[0]) === null || _j === void 0 ? void 0 : _j.trim();
                }
                const finalPath = (_m = (_l = (_k = keyRow === null || keyRow === void 0 ? void 0 : keyRow.split(' ')) === null || _k === void 0 ? void 0 : _k.filter((x) => x !== '')) === null || _l === void 0 ? void 0 : _l.slice(2)) === null || _m === void 0 ? void 0 : _m.join(' '); // remove N (typically 4) spaces between each output, then remove the ''s, then split by space again to remove Key and REG_SZ, and recombine any path with spaces
                return finalPath;
            }
            return undefined;
        });
    }
    /**
     *
     * @returns an array containing fully specified / specific versions of all globally installed sdks on the machine in windows for 32 and 64 bit sdks.
     */
    getGlobalSdkVersionsInstalledOnMachine() {
        return __awaiter(this, void 0, void 0, function* () {
            let sdks = [];
            if (os.platform() === 'win32') {
                const sdkInstallRecords64Bit = 'HKEY_LOCAL_MACHINE\\SOFTWARE\\dotnet\\Setup\\InstalledVersions\\x64\\sdk';
                const sdkInstallRecords32Bit = sdkInstallRecords64Bit.replace('x64', 'x86');
                const sdkInstallRecordsArm64 = sdkInstallRecords64Bit.replace('x64', 'arm64');
                const queries = [sdkInstallRecords32Bit, sdkInstallRecords64Bit, sdkInstallRecordsArm64];
                for (const query of queries) {
                    // /reg:32 is added because all keys on 64 bit machines are all put into the WOW node. They won't be on the WOW node on a 32 bit machine.
                    const registryLookup = yield this.queryRegistry(query, true);
                    if (registryLookup === null) {
                        return [];
                    }
                    let installRecordKeysOfXBit = '';
                    if (registryLookup.status === '0') {
                        installRecordKeysOfXBit = registryLookup.stdout;
                    }
                    const installedSdks = this.extractVersionsOutOfRegistryKeyStrings(installRecordKeysOfXBit);
                    // Append any newly found sdk versions
                    sdks = sdks.concat(installedSdks.filter((item) => sdks.indexOf(item) < 0));
                }
            }
            return sdks;
        });
    }
    queryRegistry(query, underWowNode, querySingleValue) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const registryQueryCommand = path.join(`${process.env.SystemRoot}`, `System32\\reg.exe`);
                let queryParameters = [`query`, `${query}`];
                if (underWowNode) {
                    queryParameters = [...queryParameters, `\/reg:32`];
                }
                if (querySingleValue) {
                    queryParameters = [...queryParameters, `/v`, `${querySingleValue}`];
                }
                const command = CommandExecutor_1.CommandExecutor.makeCommand(registryQueryCommand, queryParameters);
                const registryLookup = (yield this.commandRunner.execute(command, { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.DOTNET_INFORMATION_CACHE_DURATION_MS }, false));
                return registryLookup;
            }
            catch (e) {
                // There are no "X" bit sdks on the machine.
            }
            return null;
        });
    }
    /**
     *
     * @param registryQueryResult the raw output of a registry query converted into a string
     * @returns
     */
    extractVersionsOutOfRegistryKeyStrings(registryQueryResult) {
        if (registryQueryResult === '') {
            return [];
        }
        else {
            return registryQueryResult.split(' ')
                .filter(function (value, i) { return value !== '' && i !== 0; } // Filter out the whitespace & query as the query return value starts with the query.
            )
                .filter(function (value, i) { return i % 3 === 0; } // Every 0th, 4th, etc item will be a value name AKA the SDK version. The rest will be REGTYPE and REGHEXVALUE.
            );
        }
    }
}
exports.RegistryReader = RegistryReader;
//# sourceMappingURL=RegistryReader.js.map